import {
	CONVERSION_MODAL_CONFIG,
	type ConversionModalState,
	UserAction,
} from "@brookbush/types";

import { getCookie, setCookie } from "cookies-next";

import { isProgramInvitationPath } from "./programs/isProgramInvitationPath";

const COOKIE_NAME = "signup_modal_state";

/** Matches Tailwind `phablet:` — keep in sync with `--breakpoint-phablet`. */
export const SIGNUP_MODAL_PHABLET_MIN_WIDTH_PX = 768;

export type SignupModalViewport = "mobile" | "desktop";
export type SignupModalDenseVariant = "control" | "test";
export type SignupModalLayout =
	| "stacked_scroll"
	| "dense_stacked_scroll"
	| "side_by_side";

export function getSignupModalViewport(): SignupModalViewport {
	if (typeof window === "undefined") return "desktop";

	return window.matchMedia(
		`(min-width: ${SIGNUP_MODAL_PHABLET_MIN_WIDTH_PX}px)`,
	).matches
		? "desktop"
		: "mobile";
}

export function getSignupModalLayout(
	viewport = getSignupModalViewport(),
	density: SignupModalDenseVariant = "control",
): SignupModalLayout {
	if (viewport !== "mobile") return "side_by_side";

	return density === "test" ? "dense_stacked_scroll" : "stacked_scroll";
}

export function getSignupModalTrackingProperties(
	viewport = getSignupModalViewport(),
	density: SignupModalDenseVariant = "control",
): {
	viewport: SignupModalViewport;
	layout: SignupModalLayout;
	density: SignupModalDenseVariant;
} {
	return {
		viewport,
		layout: getSignupModalLayout(viewport, density),
		density,
	};
}

function getState(): ConversionModalState | null {
	const raw = getCookie(COOKIE_NAME);
	if (!raw) return null;

	try {
		return JSON.parse(raw as string);
	} catch {
		return null;
	}
}

function setState(state: ConversionModalState) {
	setCookie(COOKIE_NAME, JSON.stringify(state), {
		maxAge: 7 * 24 * 60 * 60, // 7 days
		path: "/",
		sameSite: "lax",
	});
}
export function shouldShowSignupModal(): boolean {
	const state = getState();

	if (!state) return true;

	if (state.count >= CONVERSION_MODAL_CONFIG.maxShows) return false;

	const delayMs =
		CONVERSION_MODAL_CONFIG.baseDelayMs *
		CONVERSION_MODAL_CONFIG.multiplier ** (state.count - 1);
	const nextShowTime = state.lastShown + delayMs;

	return Date.now() >= nextShowTime;
}

/**
 * Modal to open once the signup form has created an account.
 *
 * The signup form unmounts as soon as the account exists. Membership should
 * open in its place, except on program invitation pages: invitees are clients
 * of the program owner and only need to accept the invitation.
 *
 * @param options.userAction - Modal currently requested
 * @param options.isSignedIn - An account session exists
 * @param options.path - Current pathname without query string
 * @returns Membership when signup just completed, otherwise null
 */
export function userActionAfterAccountCreated(options: {
	userAction: UserAction | null;
	isSignedIn: boolean;
	path?: string | null;
}): UserAction | null {
	if (isProgramInvitationPath(options.path)) {
		return null;
	}

	if (options.userAction === UserAction.SIGN_UP && options.isSignedIn) {
		return UserAction.BECOME_A_MEMBER;
	}

	return null;
}

export function dismissSignupModal() {
	const state = getState();

	const newState: ConversionModalState = state
		? {
				lastShown: Date.now(),
				count: state.count + 1,
			}
		: {
				lastShown: Date.now(),
				count: 1,
			};

	setState(newState);
}
